import React, { useEffect, useState, useRef } from 'react';
import './index.css';
import clsx from 'classnames';
import { useAppContext } from 'context';

/**
 * 图片加载占位组件
 * @param {Object} props
 * @param {string} props.src - 图片地址
 * @param {string} props.alt - 图片描述
 * @param {number|string} props.width - 图片宽度
 * @param {number|string} props.height - 图片高度
 * @param {string} props.className - 自定义类名
 * @param {Function} props.onLoad - 图片加载完成回调
 * @param {Function} props.onError - 图片加载失败回调
 * @param {React.ReactNode} props.placeholder - 自定义占位内容
 * @param {string|number} props.cacheKey - 缓存键,当此值变化时强制重置图片加载状态(用于手动清除缓存)
 * @param {string|number} props.currentPage - 页码,用于在更换页码时重置加载中的状态
 * @param {string|number} props.id - ID,用于在切换产品内容时重置加载中的状态
 */
const ImageLoader = (props) => {
  const {
    src,
    alt = '',
    width,
    height,
    className,
    onLoad,
    onError,
    placeholder,
    cacheKey,
    currentPage, //用于在更换页码时，重置加载中的状态
    id, //用于在切换产品内容时，重置加载中的状态
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const {
    state: { type },
  } = useAppContext();

  // 处理图片加载成功
  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    onLoad && onLoad();
  };

  // 处理图片加载失败
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError && onError();
  };

  // 重置状态 - 当 cacheKey、currentPage、id 或 type 变化时
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [cacheKey, currentPage, id, type]);

  // 处理浏览器缓存导致的 onLoad 事件丢失问题
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !src) return;

    // 如果图片已经加载完成（从缓存中）
    if (img.complete) {
      // 检查图片是否有效加载
      if (img.naturalWidth !== 0) {
        // 图片加载成功
        setIsLoading(false);
        setHasError(false);
        onLoad && onLoad();
      } else {
        // 图片加载失败
        setIsLoading(false);
        setHasError(true);
        onError && onError();
      }
    }
    // 依赖项包含所有可能影响图片加载的业务参数
  }, [src, cacheKey, currentPage, id, type, onLoad, onError]);

  return (
    <div
      className={clsx('image-loader-container', className)}
      style={{ width, height }}
    >
      {/* 加载中状态 - 骨架屏 */}
      {isLoading && (
        <div className='image-loader-placeholder'>
          {placeholder ? (
            placeholder
          ) : (
            <>
              <div className='image-loader-skeleton'>
                <div className='skeleton-shimmer'></div>
              </div>
              <div className='image-loader-spinner-wrapper'>
                <div className='image-loader-spinner'></div>
              </div>
            </>
          )}
        </div>
      )}

      {/* 加载失败状态 */}
      {hasError && (
        <div className='image-loader-error'>
          <svg
            className='image-loader-error-icon'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
          >
            <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
            <circle cx='8.5' cy='8.5' r='1.5' />
            <polyline points='21 15 16 10 5 21' />
          </svg>
          <span className='image-loader-error-text'>图片加载失败</span>
        </div>
      )}
      {/* 真实图片 */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading='lazy'
        onLoad={handleLoad}
        onError={handleError}
        className={clsx('image-loader-real', {
          'image-loaded': !isLoading && !hasError,
        })}
        style={{
          opacity: isLoading || hasError ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}
      />
    </div>
  );
};

export default ImageLoader;
